import "./application.css"
import * as Sentry from '@sentry/react'
import { createInertiaApp, router } from '@inertiajs/react'
import type { ComponentType } from 'react'
import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { Toaster, toast } from 'sonner'
import { TooltipProvider } from '@/components/ui/tooltip'

if (!import.meta.env.SSR) {
  // Self-heal stale Vite chunks after a deploy. iOS standalone PWAs ("Add to
  // Home Screen") aggressively cache the root HTML and have no reload
  // affordance, so a missing chunk otherwise leaves users stuck.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    if (navigator.onLine === false) return

    const key = 'kw:preloadReloadAt'
    const now = Date.now()
    const last = Number(sessionStorage.getItem(key) || '0')
    if (now - last < 30_000) return

    sessionStorage.setItem(key, String(now))
    const url = new URL(window.location.href)
    url.searchParams.set('_v', String(now))
    window.location.replace(url.toString())
  })

  if (new URL(window.location.href).searchParams.has('_v')) {
    const cleaned = new URL(window.location.href)
    cleaned.searchParams.delete('_v')
    window.history.replaceState(window.history.state, '', cleaned.toString())
  }

  const sentryDsn = document.querySelector<HTMLMetaElement>('meta[name="sentry-dsn"]')?.content
  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: document.querySelector<HTMLMetaElement>('meta[name="sentry-environment"]')?.content ?? 'production',
      // PHI hygiene: don't attach IP, headers, cookies, or the raw user object.
      // No replayIntegration and no browserTracingIntegration: session replay
      // and performance tracing would capture medical-record screens and URLs.
      sendDefaultPii: false,
      beforeBreadcrumb(breadcrumb) {
        // Default fetch/xhr/history breadcrumbs carry full URLs. Query strings
        // hold search text and filter params, so keep only the path.
        const data = breadcrumb.data
        if (data) {
          for (const key of ['url', 'from', 'to'] as const) {
            const value = data[key]
            if (typeof value === 'string' && value.includes('?')) data[key] = value.slice(0, value.indexOf('?'))
          }
        }
        return breadcrumb
      },
      beforeSend(event, hint) {
        // Drop Inertia HttpNetworkErrors — they're never an app bug we can fix.
        // Per @inertiajs/core's xhr handlers, HttpNetworkError fires only from
        // xhr.onerror (status 0: dropped connection, offline, DNS/CORS failure);
        // a user-cancelled/superseded visit is HttpCancelledError and a real
        // server fault is HttpResponseError, so this class is exclusively a
        // transient client-side network failure (e.g. the 409 asset-version
        // reload aborting an in-flight XHR, or a flaky connection mid-visit).
        const err = hint?.originalException as Error | undefined
        if (err?.name === 'HttpNetworkError') {
          return null
        }

        // Strip query strings (may carry filter params with PHI) and request
        // body/headers/cookies; reduce user to id-only.
        if (event.request) {
          if (typeof event.request.url === 'string') {
            const qIndex = event.request.url.indexOf('?')
            if (qIndex !== -1) event.request.url = event.request.url.slice(0, qIndex)
          }
          event.request.data = undefined
          event.request.cookies = undefined
          event.request.headers = undefined
          event.request.query_string = undefined
        }
        if (event.user) {
          event.user = event.user.id ? { id: String(event.user.id) } : undefined
        }
        return event
      },
    })
  }

  // Flash → Sonner toast (Inertia v3 fires a dedicated flash event)
  router.on('flash', (event) => {
    const flash = (event as CustomEvent).detail.flash as Record<string, string | undefined>
    if (flash?.notice) toast.success(flash.notice)
    if (flash?.alert) toast.error(flash.alert, { duration: 6000 })
    if (flash?.info) toast(flash.info)
  })
}

type PageModule = { default: ComponentType<Record<string, unknown>> }
const pages = import.meta.glob<PageModule>('../pages/**/*.tsx')

// Stale-bundle self-heal: reload once with a cache buster, throttled to once
// per 30s. Returns true if a reload was triggered (caller should hang), false
// if we've already reloaded recently (caller should throw). No-op server-side.
const reloadForStaleBundle = (): boolean => {
  if (import.meta.env.SSR || typeof window === 'undefined') return false
  const key = 'kw:resolveReloadAt'
  const last = Number(sessionStorage.getItem(key) || '0')
  if (Date.now() - last <= 30_000) return false
  sessionStorage.setItem(key, String(Date.now()))
  const url = new URL(window.location.href)
  url.searchParams.set('_v', String(Date.now()))
  window.location.replace(url.toString())
  return true
}

void createInertiaApp({
  title: (title) => title || 'KeptWell',
  resolve: async (name) => {
    const importer = pages[`../pages/${name}.tsx`]
    if (!importer) {
      // Stale bundle: an old cached JS bundle's frozen import.meta.glob is
      // missing a page the server now references. Same failure mode as the
      // vite:preloadError handler above, but for the resolve step (chunk
      // loaded fine, glob is stale). Reload once with a cache buster.
      if (reloadForStaleBundle()) return new Promise<never>(() => {})
      throw new Error(`Page not found: ${name}`)
    }
    const mod = await importer()
    // The glob key existed but the lazy chunk import resolved to undefined or a
    // module with no default export — a partial/stale-bundle symptom (a chunk
    // import that already failed once can resolve empty on retry, whatwg/html#6768).
    // Self-heal with the same cache-busting reload before giving up.
    if (!mod?.default) {
      if (reloadForStaleBundle()) return new Promise<never>(() => {})
      throw new Error(`Page module has no default export: ${name}`)
    }
    return mod.default
  },

  setup({ el, App, props }) {
    const app = (
      <StrictMode>
        <Toaster position="top-center" richColors closeButton />
        <TooltipProvider delayDuration={150}>
          <App {...props} />
        </TooltipProvider>
      </StrictMode>
    )
    // Server-side: el is null. Return the element so the plugin can render it
    // to a string. Client-side: hydrate if SSR HTML is present, otherwise mount.
    if (!el) return app
    if (el.hasChildNodes()) {
      hydrateRoot(el, app)
    } else {
      createRoot(el).render(app)
    }
    return app
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: "brackets" }
    },
  },
}).catch((error) => {
  if (typeof document !== 'undefined' && document.getElementById("app")) {
    throw error
  } else if (typeof document !== 'undefined') {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  } else {
    throw error
  }
})
